import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { DOORS } from "./doors";
import { CREAM, DIM, DoorSection, Doorways, GOLD, Hero, Reveal, SANS, SERIF } from "./sections";
import { ByReclaim } from "../../components/ecosystem/ReclaimNav";
import { AdelaidaOrb } from "../../components/ecosystem/AdelaidaOrb";
import { useAuth } from "../../hooks/useAuth";
import { isStudioUser, studioHome } from "../../components/ScopedAccessGuard";

const Header = () => {
  const { user } = useAuth();
  return (
    <header className="fixed top-0 inset-x-0 z-[60] flex items-center justify-between px-5 sm:px-10 h-14" data-testid="header-ecosystem-nav"
      style={{ background: "linear-gradient(180deg, rgba(7,13,11,0.75), rgba(7,13,11,0))", backdropFilter: "blur(6px)" }}>
      <span className="text-[0.7rem] tracking-[0.55em] font-medium" style={{ color: CREAM, fontFamily: SANS }}>RECLAIM</span>
      <span className="flex items-center gap-4">
        {isStudioUser(user) && <Link to={studioHome(user)} data-testid="admin-reclaim-app-link" className="text-[0.6rem] uppercase tracking-[0.2em] rounded-full px-3 py-1" style={{ color: GOLD, border: "1px solid rgba(215,187,122,0.35)", fontFamily: SANS }}>Studio</Link>}
        <ByReclaim product="reclaim" label="The ecosystem" className="!opacity-100" />
      </span>
    </header>
  );
};

const Impact = () => (
  <div className="relative" style={{ background: "linear-gradient(180deg, #070D0B 0%, #15110B 14%, #15110B 86%, #070D0B 100%)" }} data-testid="impact-causes-section">
    <div className="max-w-4xl mx-auto px-6 sm:px-10 pt-32 pb-10 text-left sm:text-center">
      <Reveal>
        <p className="text-[0.62rem] uppercase tracking-[0.34em]" style={{ color: "#F2B860", fontFamily: SANS }}>Impact</p>
        <h2 className="mt-6 text-3xl sm:text-5xl font-light leading-tight" style={{ fontFamily: SERIF, color: CREAM }}>
          Some things we're building aren't simply businesses. <span className="italic" style={{ color: "#F2B860" }}>They're things we believe should exist.</span>
        </h2>
      </Reveal>
    </div>
    {DOORS.filter((d) => d.impact).map((d, i) => <DoorSection key={d.id} d={d} flip={i % 2 === 0} />)}
    <div className="h-24" />
  </div>
);

const LINES = ["Different problems.", "Different people.", "Different moments in life.", "One belief."];
const Manifesto = () => (
  <section className="max-w-4xl mx-auto px-6 sm:px-10 py-32 sm:py-44" data-testid="closing-manifesto-section">
    <Reveal><p className="text-[0.7rem] tracking-[0.5em] font-medium" style={{ color: GOLD, fontFamily: SANS }}>ONE ECOSYSTEM.</p></Reveal>
    <div className="mt-8">{LINES.map((l, i) => <Reveal key={l} delay={i * 0.12}><p className={`text-3xl sm:text-5xl font-light leading-snug ${i === 3 ? "italic" : ""}`} style={{ fontFamily: SERIF, color: i === 3 ? GOLD : CREAM }}>{l}</p></Reveal>)}</div>
    <Reveal delay={0.2}>
      <div className="mt-14 max-w-xl space-y-5 text-base sm:text-lg leading-relaxed" style={{ color: DIM, fontFamily: SANS }}>
        <p>Technology is becoming extraordinarily powerful. The question isn't simply what it can do. It's what we choose to do with it.</p>
        <p>We believe AI and technology can help people become calmer, more confident, more connected, more compassionate and more able to understand themselves and each other.</p>
      </div>
    </Reveal>
    <Reveal delay={0.2}><p className="mt-14 text-4xl sm:text-6xl font-light italic" style={{ fontFamily: SERIF, color: CREAM }}>More human, not less.</p></Reveal>
    <Reveal delay={0.3}>
      <div className="mt-24 pt-10 border-t" style={{ borderColor: "rgba(215,187,122,0.16)" }}>
        <p className="text-[0.75rem] tracking-[0.6em] font-medium" style={{ color: GOLD, fontFamily: SANS }}>RECLAIM</p>
        <p className="mt-3 text-2xl sm:text-3xl font-light" style={{ fontFamily: SERIF, color: CREAM }}>There is another way.</p>
      </div>
    </Reveal>
  </section>
);

export default function EcosystemHome({ friend = null }) {
  useEffect(() => { document.title = "Reclaim · There is another way"; }, []);
  return (
    <div className="min-h-screen overflow-x-hidden" style={{ background: "#070D0B", color: CREAM }} data-testid="ecosystem-home">
      <div className="pointer-events-none fixed inset-0 z-[1] opacity-[0.05] mix-blend-overlay" style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='160' height='160'%3E%3Cfilter id='n'%3E%3CfeTurbulence baseFrequency='0.8' numOctaves='3'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")" }} />
      <Header />
      <Hero />
      <Doorways />
      <div className="pb-10" data-testid="ecosystem-core-section">{DOORS.filter((d) => !d.impact).map((d, i) => <DoorSection key={d.id} d={d} flip={i % 2 === 1} />)}</div>
      <Impact />
      <Manifesto />
      <footer className="px-6 sm:px-10 pb-28 text-xs" style={{ color: "rgba(243,237,221,0.35)", fontFamily: SANS }}>© Reclaim · Using technology to help us become more human, not less.</footer>
      <AdelaidaOrb friend={friend} />
    </div>
  );
}
